/**
 * CountryPicker — searchable multi-select for ISO 3166-1 alpha-2 codes.
 *
 * Renders selected countries as removable chips and a search dropdown of the
 * remaining entries. Click a row to toggle. "All" and "None" shortcuts ship
 * because merchants often start from everywhere-then-narrow or
 * nowhere-then-add.
 */

import { useMemo, useState } from "react";
import { X, Search, ChevronDown } from "lucide-react";
import { clsx } from "clsx";
import { COUNTRIES, countryName } from "./data/countries";

interface Props {
  value: string[];
  onChange: (codes: string[]) => void;
  placeholder?: string;
}

export function CountryPicker({ value, onChange, placeholder }: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [query]);

  const toggle = (code: string) => {
    if (selectedSet.has(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const selectAll = () => onChange(COUNTRIES.map((c) => c.code));
  const clearAll = () => onChange([]);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex-1 min-w-0 flex items-center gap-2 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1.5 text-xs text-left hover:border-neutral-300 dark:hover:border-neutral-600"
          aria-expanded={open}
        >
          <Search className="w-3.5 h-3.5 text-neutral-500 shrink-0" />
          <span className="flex-1 truncate text-neutral-700 dark:text-neutral-300">
            {value.length === 0
              ? (placeholder ?? "Select countries…")
              : `${value.length} selected`}
          </span>
          <ChevronDown
            className={clsx(
              "w-3.5 h-3.5 text-neutral-500 shrink-0 transition-transform",
              open && "rotate-180",
            )}
          />
        </button>
      </div>

      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {value.map((code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded-full bg-neutral-100 dark:bg-neutral-800 pl-2 pr-1 py-0.5 text-[11px]"
              title={countryName(code)}
            >
              <span className="font-mono text-neutral-500">{code}</span>
              <span className="max-w-[120px] truncate">{countryName(code)}</span>
              <button
                type="button"
                onClick={() => toggle(code)}
                className="text-neutral-500 hover:text-category-error"
                aria-label={`Remove ${countryName(code)}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      {open ? (
        <div className="rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950">
          <div className="p-1.5 border-b border-neutral-200 dark:border-neutral-800">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or code…"
              className="w-full rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-950 px-2 py-1 text-xs"
            />
            <div className="flex items-center justify-between mt-1.5 text-[11px]">
              <span className="text-neutral-500">
                {value.length} / {COUNTRIES.length} selected
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-neutral-500 hover:text-category-error"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
          <ul
            className="max-h-64 overflow-y-auto py-1"
            role="listbox"
            aria-multiselectable
          >
            {filtered.length === 0 ? (
              <li className="px-2 py-3 text-center text-xs text-neutral-500">
                No countries match "{query}"
              </li>
            ) : (
              filtered.map((c) => {
                const selected = selectedSet.has(c.code);
                return (
                  <li key={c.code}>
                    <button
                      type="button"
                      onClick={() => toggle(c.code)}
                      className={clsx(
                        "w-full flex items-center gap-2 px-2 py-1 text-left text-xs",
                        selected
                          ? "bg-neutral-100 dark:bg-neutral-800"
                          : "hover:bg-neutral-50 dark:hover:bg-neutral-900",
                      )}
                      role="option"
                      aria-selected={selected}
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        readOnly
                        className="pointer-events-none"
                      />
                      <span className="font-mono text-[10px] text-neutral-500 w-6">
                        {c.code}
                      </span>
                      <span className="flex-1 truncate">{c.name}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
